import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import InfoIcon from "../images/icons/info.svg";
import Video from "./Video";
import Modal from "./Modal";
import ToggleInput from "./inputs/ToggleInput";
import DrawingInput from "./inputs/DrawingInput";
import { getMediaType } from "../utils/MediaOperations";

// BigInt JSON serialization support
/* global BigInt */
if (typeof BigInt !== 'undefined') {
  BigInt.prototype.toJSON = function () {
    // Use JSON.rawJSON if available (for proper number serialization without quotes)
    // Otherwise fallback to string representation
    if (typeof JSON.rawJSON !== 'undefined') {
      return JSON.rawJSON(this.toString());
    }
    return this.toString();
  };
}

const toFileUrl = (filePath) => {
  return filePath;
}

const formatLabelText = (text) => text
  .split('_')
  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
  .join(' ');

// Helper function to check if the model requires a mask
const checkIfModelRequiresMask = (schema) => {
  if (!schema || typeof schema !== 'object') {
    return false;
  }

  return Object.values(schema).some(field => {
    return (
      field.type === 'b64data' &&
      field.importance === 5 &&
      (field.require_mask === true || field.require_alpha === true)
    );
  });
};

// Helper function to find mask fields by their schema properties (not names)
const findMaskFields = (schema) => {
  if (!schema || typeof schema !== 'object') {
    return [];
  }

  const maskFields = [];
  for (const [key, field] of Object.entries(schema)) {
    if (field.type === 'b64data' &&
        (field.require_mask === true)) {
      maskFields.push(key);
    }
  }
  return maskFields;
};

// Helper function to generate random seed
const generateRandomSeed = () => {
  // Generate a random 64-bit number using BigInt
  const randomBytes = new Uint8Array(8);
  crypto.getRandomValues(randomBytes);
  // Convert bytes to BigInt
  let randomBigInt = 0n;
  for (let i = 0; i < 8; i++) {
    randomBigInt = (randomBigInt << 8n) | BigInt(randomBytes[i]);
  }
  // Ensure positive and within 64-bit signed range (0 to 2^63-1)
  const maxInt64 = (1n << 63n) - 1n;
  randomBigInt = randomBigInt & maxInt64;
  return randomBigInt.toString();
};

// Simple seed field component helper functions
const calculateSeedValue = (mode, currentValue) => {
  const current = parseInt(currentValue || '0', 10);
  // console.log(`[calculateSeedValue] Mode: ${mode}, Current value: ${currentValue}, Parsed: ${current}`);
  
  switch (mode) {
    case 'increment':
      const incremented = (current + 1).toString();
      // console.log(`[calculateSeedValue] Incrementing to: ${incremented}`);
      return incremented;
    case 'decrement':
      const decremented = Math.max(0, current - 1).toString();
      // console.log(`[calculateSeedValue] Decrementing to: ${decremented}`);
      return decremented;
    case 'random':
      // For random mode, return the provided value (which should be the saved seed from DB)
      // console.log(`[calculateSeedValue] Random mode: using provided value: ${currentValue || '0'}`);
      return currentValue || '0';
    default:
      return currentValue || '0';
  }
};

// Helper function to round a value to the nearest step-divisible number
const roundToNearestStep = (value, min, step) => {
  if (!step || step <= 0) return value;

  const numValue = parseFloat(value);
  const numMin = parseFloat(min || 0);
  const numStep = parseFloat(step);

  if (isNaN(numValue) || isNaN(numStep)) return value;

  // Calculate how many steps from the minimum
  const stepsFromMin = Math.round((numValue - numMin) / numStep);

  // Calculate the nearest step-divisible value
  const nearestValue = numMin + (stepsFromMin * numStep);

  return nearestValue;
};

// Minimal seed field component to handle mode changes
const SeedField = ({ field, mode, seedKey, onFieldChange, onFetchLatestSeed, fieldType }) => {
  const prevModeRef = React.useRef(mode);
  const [isLoading, setIsLoading] = React.useState(false);
  
  React.useEffect(() => {
    // Only calculate if mode actually changed
    if (mode !== prevModeRef.current && mode !== 'fixed') {
      // console.log(`[SeedField] Mode changed from ${prevModeRef.current} to ${mode} for field ${seedKey}`);
      
      // For random, increment, and decrement modes, fetch the latest seed from DB
      if ((mode === 'random' || mode === 'increment' || mode === 'decrement') && onFetchLatestSeed) {
        // console.log(`[SeedField] Fetching latest seed for ${mode} mode, field key: ${seedKey}`);
        setIsLoading(true);
        
        onFetchLatestSeed(seedKey).then(latestSeed => {
          // console.log(`[SeedField] Fetched latest seed for field '${seedKey}': ${latestSeed}`);
          
          // Validate the fetched seed
          if (!latestSeed || latestSeed === '' || latestSeed === 'NaN' || isNaN(parseInt(latestSeed))) {
            console.warn(`[SeedField] Invalid seed fetched: '${latestSeed}', using current value as fallback`);
            const currentValue = field.value || '0';
            const newValue = calculateSeedValue(mode, currentValue);
            field.onChange(newValue);
            if (onFieldChange) {
              onFieldChange(seedKey, newValue);
            }
          } else {
            const newValue = calculateSeedValue(mode, latestSeed);
            field.onChange(newValue);
            if (onFieldChange) {
              onFieldChange(seedKey, newValue);
            }
          }
          setIsLoading(false);
        }).catch(error => {
          console.error(`[SeedField] Error fetching latest seed for field '${seedKey}':`, error);
          // Fallback to current value
          const newValue = calculateSeedValue(mode, field.value || '0');
          field.onChange(newValue);
          if (onFieldChange) {
            onFieldChange(seedKey, newValue);
          }
          setIsLoading(false);
        });
      } else {
        // Only for modes without DB fetch or if no fetch function provided
        // console.log(`[SeedField] Using ${mode} mode without DB fetch`);
        const newValue = calculateSeedValue(mode, field.value || '0');
        field.onChange(newValue);
        if (onFieldChange) {
          onFieldChange(seedKey, newValue);
        }
      }
    }
    // Update the previous mode reference
    prevModeRef.current = mode;
  }, [mode, field, seedKey, onFieldChange, onFetchLatestSeed]);
  
  return (
    <div className="mb-4">
      {mode === 'fixed' ? (
        <input 
          type="text" 
          maxLength="15"
          pattern="[0-9]*"
          inputMode="numeric"
          className="w-full p-3 rounded-lg bg-dark-purple/40 border border-white/20 text-white text-base placeholder-white/50 focus:border-yellow focus:ring-1 focus:ring-yellow/50 focus:bg-dark-purple/50 transition-all" 
          placeholder="Enter seed"
          name={seedKey}
          {...field}
          value={field.value ?? (fieldType === 'STRING' ? '' : '0')}
          onChange={(e) => {
            // Only allow numeric input and limit to 15 digits
            const value = e.target.value.replace(/\D/g, '').slice(0, 15);
            // Keep as string to preserve large numbers
            field.onChange(value || null);
          }}
        />
      ) : (
        <div className="w-full p-3 rounded-lg bg-dark-purple/20 border border-white/10 text-white text-base">
          {isLoading ? (
            <span className="text-yellow animate-pulse">Loading latest seed...</span>
          ) : (
            <>
              <span className="text-yellow">
                {mode === 'increment' ? 'Will increment from: ' : 
                 mode === 'decrement' ? 'Will decrement from: ' : 
                 mode === 'random' ? 'Will use saved seed: ' :
                 'Will use: '}
              </span>
              {field.value || (fieldType === 'STRING' ? '' : '0')}
              {(mode === 'increment' || mode === 'decrement') && (
                <span className="text-white/60 ml-2 text-sm">
                  ({mode === 'increment' ? '+1' : '-1'})
                </span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Textarea component for STRING fields
const StringTextarea = ({ field, label, tooltip, fieldKey, onFieldChange, maxLength }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [showLimitTooltip, setShowLimitTooltip] = useState(false);
  const tooltipTimeoutRef = useRef(null);
  const textLength = field.value?.length || 0;
  const isLongText = textLength > 100;

  // Character limit logic
  const hasLimit = maxLength && maxLength > 0;
  const limitPercentage = hasLimit ? (textLength / maxLength) * 100 : 0;

  // Color coding based on limit percentage
  const getCharCountColor = () => {
    if (!hasLimit) return 'text-white/50';
    if (limitPercentage < 80) return 'text-green-400';
    if (limitPercentage < 95) return 'text-yellow-400';
    return 'text-red-400';
  };
  
  // Detect field types for special styling
  const isPromptField = fieldKey.toLowerCase().includes('prompt') || fieldKey.toLowerCase().includes('tags');
  const isLyricsField = fieldKey.toLowerCase().includes('lyrics');
  const isDescriptionField = fieldKey.toLowerCase().includes('description') || fieldKey.toLowerCase().includes('desc');
  
  // Choose appropriate icon and styling
  const getFieldIcon = () => {
    if (isPromptField) return '✨';
    if (isLyricsField) return '🎵';
    if (isDescriptionField) return '📄';
    return '📝';
  };
  
  const getFieldColor = () => {
    if (isPromptField) return 'border-yellow/40 focus:border-yellow';
    if (isLyricsField) return 'border-purple-400/40 focus:border-purple-400';
    if (isDescriptionField) return 'border-blue-400/40 focus:border-blue-400';
    return 'border-white/20 focus:border-yellow';
  };

  return (
    <div className={`mb-4 ${(isPromptField || isLyricsField) ? 'col-span-full' : ''}`}>
      <label className="w-full flex flex-row gap-3 items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-lg">{getFieldIcon()}</span>
          <span className="text-yellow font-medium text-base">{label}</span>
          {tooltip && <span className="opacity-70 text-sm">{tooltip}</span>}
          {isPromptField && <span className="text-sm bg-yellow/20 text-yellow px-2 py-1 rounded">PROMPT</span>}
          {isLyricsField && <span className="text-sm bg-purple-400/20 text-purple-300 px-2 py-1 rounded">LYRICS</span>}
        </div>
        <div className="flex items-center gap-2">
          {(textLength > 0 || hasLimit) && (
            <span className={`text-sm font-mono ${getCharCountColor()}`}>
              {hasLimit ? `${textLength}/${maxLength} chars` : `${textLength} chars`}
            </span>
          )}
          <button
            type="button"
            onClick={() => setShowFullScreen(true)}
            className="text-yellow/70 hover:text-yellow transition-colors text-base p-2 rounded hover:bg-yellow/10"
            title="Expand to full screen"
          >
            ⛶
          </button>
        </div>
      </label>
      
      <div className="relative group">
        <textarea 
          className={`w-full p-4 rounded-lg border-2 text-white placeholder-white/50 
            focus:ring-2 focus:ring-yellow/30
            transition-all duration-300 resize-none font-medium leading-relaxed text-base
            ${isExpanded ? 'min-h-[200px]' : 'min-h-[140px]'}
            hover:border-white/30
            ${isPromptField ? 'bg-dark-purple hover:bg-medium-blue focus:bg-medium-blue font-medium' : 
              isLyricsField ? 'bg-dark-purple hover:bg-medium-blue focus:bg-medium-blue font-normal leading-7' : 
              isDescriptionField ? 'bg-dark-purple hover:bg-medium-blue focus:bg-medium-blue font-medium' :
              'bg-dark-purple hover:bg-medium-blue focus:bg-medium-blue font-medium'}
            ${getFieldColor()}`}
          placeholder={`Enter ${label.toLowerCase()}...`}
          onFocus={() => setIsExpanded(true)}
          onBlur={() => !isLongText && setIsExpanded(false)}
          style={{
            minHeight: isExpanded ? '240px' : (isPromptField || isLyricsField) ? '160px' : '140px',
            transition: 'min-height 0.3s ease, border-color 0.3s ease'
          }}
          {...field}
          value={field.value ?? ''}
          maxLength={hasLimit ? maxLength : undefined}
          onChange={(e) => {
            let newValue = e.target.value;

            // Handle character limit enforcement and tooltip display
            if (hasLimit && e.target.value.length > maxLength) {
              // User tried to exceed limit - show tooltip and truncate
              setShowLimitTooltip(true);

              // Clear existing timeout
              if (tooltipTimeoutRef.current) {
                clearTimeout(tooltipTimeoutRef.current);
              }

              // Auto-hide after 3 seconds
              tooltipTimeoutRef.current = setTimeout(() => {
                setShowLimitTooltip(false);
              }, 3000);

              // Truncate to limit
              newValue = newValue.substring(0, maxLength);
            } else if (hasLimit && e.target.value.length === maxLength) {
              // User is exactly at limit - show tooltip but allow the text
              setShowLimitTooltip(true);

              // Clear existing timeout
              if (tooltipTimeoutRef.current) {
                clearTimeout(tooltipTimeoutRef.current);
              }

              // Auto-hide after 3 seconds
              tooltipTimeoutRef.current = setTimeout(() => {
                setShowLimitTooltip(false);
              }, 3000);
            } else {
              // User is below limit - hide tooltip
              setShowLimitTooltip(false);
              if (tooltipTimeoutRef.current) {
                clearTimeout(tooltipTimeoutRef.current);
              }
            }

            field.onChange(newValue);
            if (onFieldChange) {
              onFieldChange(fieldKey, newValue);
            }
          }}
        />

        {/* Character limit tooltip */}
        {showLimitTooltip && (
          <div className="absolute top-2 right-2 z-50 bg-red-500/90 text-white font-medium text-sm px-3 py-2 rounded-lg border border-red-400 shadow-lg transition-all duration-300 ease-in-out opacity-100 scale-100">
            <div className="flex items-center gap-2">
              <span>⚠️</span>
              <div>
                <div>Character limit reached!</div>
                <div className="text-xs opacity-90">{maxLength} character maximum</div>
              </div>
            </div>
          </div>
        )}

        {/* Auto-resize handle */}
        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-50 transition-opacity">
          <div className="w-3 h-3 border-r-2 border-b-2 border-white/30"></div>
        </div>
      </div>
      
      {/* Full Screen Modal */}
      {showFullScreen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 z-[9999] bg-dark-blue/90 backdrop-blur overflow-y-auto"
          onClick={() => setShowFullScreen(false)}
        >
          <div className="min-h-screen flex items-center justify-center p-4">
            <div 
              className="bg-dark-blue border border-yellow/30 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
            {/* Full screen header */}
            <div className="bg-dark-purple/50 px-6 py-4 border-b border-yellow/20 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <span className="text-yellow text-xl">{getFieldIcon()}</span>
                <h3 className="text-yellow font-bold text-lg">Editing: {label}</h3>
                {isPromptField && <span className="text-sm bg-yellow/20 text-yellow px-2 py-1 rounded">PROMPT</span>}
                {isLyricsField && <span className="text-sm bg-purple-400/20 text-purple-300 px-2 py-1 rounded">LYRICS</span>}
                <span className={`text-sm font-mono ${getCharCountColor()}`}>
                  {hasLimit ? `${textLength}/${maxLength} characters` : `${textLength} characters`}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowFullScreen(false)}
                className="px-3 py-2 bg-yellow/20 hover:bg-yellow/30 text-yellow rounded-lg transition-all"
              >
                ✕
              </button>
            </div>
            
            {/* Full screen content */}
            <div className="p-6 relative">
              <textarea
                className={`w-full h-[60vh] p-4 rounded-lg bg-dark-purple border border-white/20 text-white placeholder-white/50
                  focus:border-yellow focus:ring-1 focus:ring-yellow/50 focus:bg-medium-blue resize-none font-medium leading-relaxed text-base text-lg`}
                placeholder={`Enter ${label.toLowerCase()}... (Full screen editing mode)`}
                {...field}
                maxLength={hasLimit ? maxLength : undefined}
                onChange={(e) => {
                  let newValue = e.target.value;

                  // Handle character limit enforcement and tooltip display
                  if (hasLimit && e.target.value.length > maxLength) {
                    // User tried to exceed limit - show tooltip and truncate
                    setShowLimitTooltip(true);

                    // Clear existing timeout
                    if (tooltipTimeoutRef.current) {
                      clearTimeout(tooltipTimeoutRef.current);
                    }

                    // Auto-hide after 3 seconds
                    tooltipTimeoutRef.current = setTimeout(() => {
                      setShowLimitTooltip(false);
                    }, 3000);

                    // Truncate to limit
                    newValue = newValue.substring(0, maxLength);
                  } else if (hasLimit && e.target.value.length === maxLength) {
                    // User is exactly at limit - show tooltip but allow the text
                    setShowLimitTooltip(true);

                    // Clear existing timeout
                    if (tooltipTimeoutRef.current) {
                      clearTimeout(tooltipTimeoutRef.current);
                    }

                    // Auto-hide after 3 seconds
                    tooltipTimeoutRef.current = setTimeout(() => {
                      setShowLimitTooltip(false);
                    }, 3000);
                  } else {
                    // User is below limit - hide tooltip
                    setShowLimitTooltip(false);
                    if (tooltipTimeoutRef.current) {
                      clearTimeout(tooltipTimeoutRef.current);
                    }
                  }

                  field.onChange(newValue);
                  if (onFieldChange) {
                    onFieldChange(fieldKey, newValue);
                  }
                }}
                autoFocus
              />

              {/* Character limit tooltip for full-screen mode */}
              {showLimitTooltip && (
                <div className="absolute top-8 right-8 z-50 bg-red-500/90 text-white font-medium text-sm px-3 py-2 rounded-lg border border-red-400 shadow-lg transition-all duration-300 ease-in-out opacity-100 scale-100">
                  <div className="flex items-center gap-2">
                    <span>⚠️</span>
                    <div>
                      <div>Character limit reached!</div>
                      <div className="text-xs opacity-90">{maxLength} character maximum</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Full screen footer */}
            <div className="bg-dark-purple/30 px-6 py-4 border-t border-yellow/20 flex justify-between items-center">
              <div className="text-white/60 text-base">
                💡 Use this space for detailed text editing
              </div>
              <button
                type="button"
                onClick={() => setShowFullScreen(false)}
                className="px-6 py-2 bg-yellow hover:bg-yellow/90 text-dark-blue font-medium rounded-lg transition-all"
              >
                Done
              </button>
            </div>
          </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Prompt textarea component
const PromptTextarea = ({ field, label, tooltip, maxLength }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [showLimitTooltip, setShowLimitTooltip] = useState(false);
  const tooltipTimeoutRef = useRef(null);
  const textLength = field.value?.length || 0;
  const isLongText = textLength > 100;

  // Character limit logic
  const hasLimit = maxLength && maxLength > 0;
  const limitPercentage = hasLimit ? (textLength / maxLength) * 100 : 0;

  // Color coding based on limit percentage
  const getCharCountColor = () => {
    if (!hasLimit) return 'text-white/50';
    if (limitPercentage < 80) return 'text-green-400';
    if (limitPercentage < 95) return 'text-yellow-400';
    return 'text-red-400';
  };
  
  return (
    <div className="mb-4 col-span-full">
      <label className="w-full flex flex-row gap-3 items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-lg">✨</span>
          <span className="text-yellow font-medium text-base">{label}</span>
          {tooltip && <span className="opacity-70 text-sm">{tooltip}</span>}
          <span className="text-sm bg-yellow/20 text-yellow px-2 py-1 rounded">AI PROMPT</span>
        </div>
        <div className="flex items-center gap-2">
          {(textLength > 0 || hasLimit) && (
            <span className={`text-sm font-mono ${getCharCountColor()}`}>
              {hasLimit ? `${textLength}/${maxLength} chars` : `${textLength} chars`}
            </span>
          )}
          <button
            type="button"
            onClick={() => setShowFullScreen(true)}
            className="text-yellow/70 hover:text-yellow transition-colors text-base p-2 rounded hover:bg-yellow/10"
            title="Expand to full screen"
          >
            ⛶
          </button>
        </div>
      </label>
      
      <div className="relative group">
        <textarea 
          className={`w-full p-5 rounded-lg bg-dark-purple border-2 border-yellow/40 text-white placeholder-white/50 
            focus:border-yellow focus:ring-2 focus:ring-yellow/30 focus:bg-medium-blue
            transition-all duration-300 resize-none font-medium leading-relaxed text-base
            ${isExpanded ? 'min-h-[280px]' : 'min-h-[180px]'}
            hover:border-yellow/60 hover:bg-medium-blue text-base`}
          placeholder="Enter your AI prompt here... Be specific and descriptive for best results."
          onFocus={() => setIsExpanded(true)}
          onBlur={() => !isLongText && setIsExpanded(false)}
          style={{
            minHeight: isExpanded ? '280px' : '180px',
            transition: 'min-height 0.3s ease, border-color 0.3s ease'
          }}
          {...field}
          value={field.value ?? ''}
          maxLength={hasLimit ? maxLength : undefined}
          onChange={(e) => {
            let newValue = e.target.value;

            // Handle character limit enforcement and tooltip display
            if (hasLimit && e.target.value.length > maxLength) {
              // User tried to exceed limit - show tooltip and truncate
              setShowLimitTooltip(true);

              // Clear existing timeout
              if (tooltipTimeoutRef.current) {
                clearTimeout(tooltipTimeoutRef.current);
              }

              // Auto-hide after 3 seconds
              tooltipTimeoutRef.current = setTimeout(() => {
                setShowLimitTooltip(false);
              }, 3000);

              // Truncate to limit
              newValue = newValue.substring(0, maxLength);
            } else if (hasLimit && e.target.value.length === maxLength) {
              // User is exactly at limit - show tooltip but allow the text
              setShowLimitTooltip(true);

              // Clear existing timeout
              if (tooltipTimeoutRef.current) {
                clearTimeout(tooltipTimeoutRef.current);
              }

              // Auto-hide after 3 seconds
              tooltipTimeoutRef.current = setTimeout(() => {
                setShowLimitTooltip(false);
              }, 3000);
            } else {
              // User is below limit - hide tooltip
              setShowLimitTooltip(false);
              if (tooltipTimeoutRef.current) {
                clearTimeout(tooltipTimeoutRef.current);
              }
            }

            field.onChange(newValue);
          }}
        />

        {/* Character limit tooltip */}
        {showLimitTooltip && (
          <div className="absolute top-2 right-2 z-50 bg-red-500/90 text-white font-medium text-sm px-3 py-2 rounded-lg border border-red-400 shadow-lg transition-all duration-300 ease-in-out opacity-100 scale-100">
            <div className="flex items-center gap-2">
              <span>⚠️</span>
              <div>
                <div>Character limit reached!</div>
                <div className="text-xs opacity-90">{maxLength} character maximum</div>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced prompt suggestions */}
        <div className="absolute bottom-2 left-2 flex gap-2 opacity-0 group-hover:opacity-70 transition-opacity">
          <span className="text-sm text-yellow/60">💡 Pro tip: Use descriptive adjectives</span>
        </div>
        
        {/* Auto-resize handle */}
        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-50 transition-opacity">
          <div className="w-3 h-3 border-r-2 border-b-2 border-yellow/50"></div>
        </div>
      </div>
      
      {/* Full Screen Modal for prompts */}
      {showFullScreen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 z-[9999] bg-dark-blue/90 backdrop-blur overflow-y-auto"
          onClick={() => setShowFullScreen(false)}
        >
          <div className="min-h-screen flex items-center justify-center p-4">
            <div 
              className="bg-dark-blue border border-yellow/30 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
            {/* Full screen header */}
            <div className="bg-gradient-to-r from-yellow/10 to-yellow/5 px-6 py-4 border-b border-yellow/20 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <span className="text-yellow text-xl">✨</span>
                <h3 className="text-yellow font-bold text-xl">AI Prompt Editor</h3>
                <span className="text-sm bg-yellow/20 text-yellow px-3 py-1 rounded-full">ENHANCED MODE</span>
                <span className={`text-sm font-mono ${getCharCountColor()}`}>
                  {hasLimit ? `${textLength}/${maxLength} characters` : `${textLength} characters`}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowFullScreen(false)}
                className="px-3 py-2 bg-yellow/20 hover:bg-yellow/30 text-yellow rounded-lg transition-all"
              >
                ✕
              </button>
            </div>
            
            {/* Full screen content */}
            <div className="p-6 relative">
              <textarea
                className="w-full h-[60vh] p-4 rounded-lg bg-dark-purple border border-yellow/30 text-white placeholder-white/50
                  focus:border-yellow focus:ring-1 focus:ring-yellow/50 focus:bg-medium-blue resize-none font-medium leading-relaxed text-base text-lg"
                placeholder="Craft your perfect prompt here...

Tips for better results:
• Be specific about what you want
• Include style preferences
• Mention any important details
• Use descriptive language"
                {...field}
                maxLength={hasLimit ? maxLength : undefined}
                onChange={(e) => {
                  let newValue = e.target.value;

                  // Handle character limit enforcement and tooltip display
                  if (hasLimit && e.target.value.length > maxLength) {
                    // User tried to exceed limit - show tooltip and truncate
                    setShowLimitTooltip(true);

                    // Clear existing timeout
                    if (tooltipTimeoutRef.current) {
                      clearTimeout(tooltipTimeoutRef.current);
                    }

                    // Auto-hide after 3 seconds
                    tooltipTimeoutRef.current = setTimeout(() => {
                      setShowLimitTooltip(false);
                    }, 3000);

                    // Truncate to limit
                    newValue = newValue.substring(0, maxLength);
                  } else if (hasLimit && e.target.value.length === maxLength) {
                    // User is exactly at limit - show tooltip but allow the text
                    setShowLimitTooltip(true);

                    // Clear existing timeout
                    if (tooltipTimeoutRef.current) {
                      clearTimeout(tooltipTimeoutRef.current);
                    }

                    // Auto-hide after 3 seconds
                    tooltipTimeoutRef.current = setTimeout(() => {
                      setShowLimitTooltip(false);
                    }, 3000);
                  } else {
                    // User is below limit - hide tooltip
                    setShowLimitTooltip(false);
                    if (tooltipTimeoutRef.current) {
                      clearTimeout(tooltipTimeoutRef.current);
                    }
                  }

                  field.onChange(newValue);
                }}
                autoFocus
              />

              {/* Character limit tooltip for full-screen mode */}
              {showLimitTooltip && (
                <div className="absolute top-8 right-8 z-50 bg-red-500/90 text-white font-medium text-sm px-3 py-2 rounded-lg border border-red-400 shadow-lg transition-all duration-300 ease-in-out opacity-100 scale-100">
                  <div className="flex items-center gap-2">
                    <span>⚠️</span>
                    <div>
                      <div>Character limit reached!</div>
                      <div className="text-xs opacity-90">{maxLength} character maximum</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Full screen footer */}
            <div className="bg-gradient-to-r from-yellow/10 to-transparent px-6 py-4 border-t border-yellow/20 flex justify-between items-center">
              <div className="text-white/60 text-sm flex items-center gap-2">
                <span className="text-yellow">💡</span>
                Use vivid descriptions for best AI results
              </div>
              <button
                type="button"
                onClick={() => setShowFullScreen(false)}
                className="px-6 py-2 bg-yellow hover:bg-yellow/90 text-dark-blue font-medium rounded-lg transition-all"
              >
                Done Editing
              </button>
            </div>
          </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// URL Input Component with preview functionality
const URLInputComponent = ({ field, label, tooltip, onFieldChange, fieldKey }) => {
  const [isValidUrl, setIsValidUrl] = useState(false);
  const [mediaType, setMediaType] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // URL validation helper
  const validateUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Media type detection helper
  const detectMediaType = (url) => {
    const extension = url.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension)) return 'image';
    if (['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(extension)) return 'video';
    if (['mp3', 'wav', 'ogg', 'm4a', 'flac'].includes(extension)) return 'audio';
    return 'unknown';
  };

  // Validate URL when field.value changes (from external sources like useImage)
  useEffect(() => {
    const url = field.value;
    if (url) {
      const valid = validateUrl(url);
      setIsValidUrl(valid);

      if (valid) {
        const type = detectMediaType(url);
        setMediaType(type);
        setPreviewError(false);
      } else {
        setMediaType(null);
        setPreviewError(false);
      }
    } else {
      setIsValidUrl(false);
      setMediaType(null);
      setPreviewError(false);
    }
  }, [field.value]);

  // Handle URL input changes
  const handleUrlChange = (e) => {
    const url = e.target.value;
    field.onChange(url);

    if (onFieldChange) {
      onFieldChange(fieldKey, url);
    }
  };

  // Preview component for different media types
  const PreviewComponent = ({ url, type }) => {
    if (!url || !isValidUrl) return null;

    const handleLoadError = () => {
      setPreviewError(true);
      setIsLoading(false);
    };

    const handleLoadStart = () => {
      setIsLoading(true);
      setPreviewError(false);
    };

    const handleLoadSuccess = () => {
      setIsLoading(false);
      setPreviewError(false);
    };

    switch (type) {
      case 'image':
        return (
          <div className="mt-3 text-center">
            <div className="inline-block relative group">
              <img
                src={url}
                alt="URL Preview"
                className="w-[250px] h-[250px] object-cover rounded-lg shadow-lg border-2 border-yellow/30"
                onLoad={handleLoadSuccess}
                onError={handleLoadError}
                onLoadStart={handleLoadStart}
              />
              <div className="absolute top-2 right-2 bg-dark-blue/80 px-2 py-1 rounded text-xs text-yellow flex items-center gap-1">
                <span>🔗</span>
                <span>URL Image</span>
              </div>
            </div>
            {isLoading && (
              <p className="text-white/60 text-sm mt-2">Loading preview...</p>
            )}
            {previewError && (
              <p className="text-red-400 text-sm mt-2">Failed to load image preview</p>
            )}
          </div>
        );

      case 'video':
        return (
          <div className="mt-3 text-center">
            <div className="inline-block relative group">
              <video
                src={url}
                className="w-[300px] h-auto rounded-lg border-2 border-yellow/30 shadow-lg"
                controls
                onLoadedData={handleLoadSuccess}
                onCanPlay={handleLoadSuccess}
                onCanPlayThrough={handleLoadSuccess}
                onError={handleLoadError}
              />
              <div className="absolute top-2 right-2 bg-dark-blue/80 px-2 py-1 rounded text-xs text-yellow flex items-center gap-1">
                <span>🔗</span>
                <span>URL Video</span>
              </div>
            </div>
            {previewError && (
              <p className="text-red-400 text-sm mt-2">Failed to load video preview</p>
            )}
          </div>
        );

      case 'audio':
        return (
          <div className="mt-3 text-center">
            <div className="inline-block relative group">
              <div className="bg-dark-purple/40 p-4 rounded-lg border border-yellow/30">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl">🎵</span>
                  <span className="text-yellow font-medium">URL Audio File</span>
                </div>
                <audio
                  src={url}
                  controls
                  className="max-w-full"
                  onLoadedData={handleLoadSuccess}
                  onCanPlay={handleLoadSuccess}
                  onCanPlayThrough={handleLoadSuccess}
                  onError={handleLoadError}
                />
              </div>
              <div className="absolute top-2 right-2 bg-dark-blue/80 px-2 py-1 rounded text-xs text-yellow flex items-center gap-1">
                <span>🔗</span>
                <span>URL Audio</span>
              </div>
            </div>
            {previewError && (
              <p className="text-red-400 text-sm mt-2">Failed to load audio preview</p>
            )}
          </div>
        );

      default:
        return (
          <div className="mt-3 text-center">
            <div className="bg-dark-purple/40 p-4 rounded-lg border border-white/20">
              <div className="flex items-center gap-3 justify-center">
                <span className="text-2xl">🔗</span>
                <span className="text-white/70">Valid URL - Preview not available</span>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="mb-4">
      <label className="w-full flex flex-row gap-3 items-center mb-3">
        <span className="text-yellow font-medium text-base">{label}</span>
        {tooltip && <span className="opacity-70 text-sm">{tooltip}</span>}
      </label>

      <input
        type="url"
        className={`w-full p-3 rounded-lg bg-dark-purple/40 border text-white text-base placeholder-white/50 focus:border-yellow focus:ring-1 focus:ring-yellow/50 focus:bg-dark-purple/50 transition-all ${
          field.value && !isValidUrl ? 'border-red-400' : 'border-white/20'
        }`}
        placeholder="Enter media URL (e.g., https://example.com/image.jpg)"
        value={field.value || ''}
        onChange={handleUrlChange}
      />

      {field.value && !isValidUrl && (
        <p className="text-red-400 text-sm mt-2">Please enter a valid URL</p>
      )}

      {field.value && isValidUrl && (
        <p className="text-green-400 text-sm mt-2">✓ Valid URL detected</p>
      )}

      <PreviewComponent url={field.value} type={mediaType} />
    </div>
  );
};

const DynamicPromptForm = forwardRef(({ schema, initialValues, isComfyUIModel = true, selectedModel, onFieldChange, onFetchLatestSeed, uploadedB64Data = {}, onB64Upload }, ref) => {
    // Helper to detect if the current model is a cosy model
    const isCosyModel = selectedModel?.includes('(cosy)') || false;
    
    // console.log('[DynamicPromptForm] Component rendered with props:', {
    //   schemaKeys: schema ? Object.keys(schema) : 'no schema',
    //   hasScheduler: schema && 'scheduler' in schema,
    //   schedulerSchema: schema?.scheduler,
    //   schedulerOptions: schema?.scheduler?.options,
    //   schedulerOptionsLength: schema?.scheduler?.options?.length,
    //   isComfyUIModel,
    //   selectedModel,
    //   isCosyModel,
    //   initialValuesKeys: initialValues ? Object.keys(initialValues) : 'no initial values',
    //   schedulerInitialValue: initialValues?.scheduler,
    //   hasOnFieldChange: !!onFieldChange,
    //   seed_mode_value: initialValues?.seed_mode || 'NOT IN INITIAL VALUES',
    //   seed_value: initialValues?.seed || 'NO SEED'
    // });
    
    // Generate default values based on schema if not provided
    const getDefaultValues = () => {
      const baseValues = initialValues && Object.keys(initialValues).length > 0 ? {...initialValues} : {};
      
      // Override with uploaded b64data values
      if (uploadedB64Data && Object.keys(uploadedB64Data).length > 0) {
        Object.entries(uploadedB64Data).forEach(([key, value]) => {
          if (value) {
            baseValues[key] = value;
          }
        });
      }
      
      const defaults = baseValues;
      if (schema) {
        Object.entries(schema).forEach(([key, mapping]) => {
          const type = mapping.type;
          switch (type) {
            case 'FLOAT':
              defaults[key] = mapping.default ?? mapping.min ?? 0;
              break;
            case 'INT':
            case 'number':
              defaults[key] = mapping.default ?? mapping.min ?? 1;
              break;
            case 'BOOLEAN':
            case 'boolean':
            case 'BOOL':
            case 'bool':
              // Ensure we always get a boolean value, defaulting to false
              if (mapping.default !== undefined) {
                defaults[key] = mapping.default === true || 
                               mapping.default === "true" || 
                               mapping.default === 1 || 
                               mapping.default === "1";
              } else {
                defaults[key] = false; // Explicitly default to false
              }
              break;
            case 'seed':
              // Ensure seed default is a string and limited to 15 digits
              const seedDefault = String(mapping.default ?? '0');
              defaults[key] = seedDefault.replace(/\D/g, '').slice(0, 15) || '0';
              break;
            case 'STRING':
            case 'prompt':
            case 'text':
            case 'url':
              defaults[key] = mapping.default ?? '';
              break;
            case 'enum':
            case 'COMBO':
              // Handle index-based defaults for enum fields
              if (mapping.default !== undefined && mapping.options) {
                if (typeof mapping.default === 'number' && mapping.default >= 0 && mapping.default < mapping.options.length) {
                  defaults[key] = mapping.options[mapping.default]; // Convert index to value
                  // console.log(`[ENUM DEFAULT] Converted index ${mapping.default} to value '${defaults[key]}' for field '${key}'`);
                } else if (typeof mapping.default === 'string') {
                  defaults[key] = mapping.default; // Use string default as-is
                  // console.log(`[ENUM DEFAULT] Using string default '${mapping.default}' for field '${key}'`);
                } else {
                  defaults[key] = mapping.options?.[0] || ''; // Fallback to first option
                  // console.log(`[ENUM DEFAULT] Invalid default (${mapping.default}), using first option '${defaults[key]}' for field '${key}'`);
                }
              } else {
                defaults[key] = mapping.options?.[0] || ''; // Fallback to first option
                // console.log(`[ENUM DEFAULT] No default specified, using first option '${defaults[key]}' for field '${key}'`);
              }
              break;
            case 'b64data':
            case 'IMAGE':
              defaults[key] = mapping.default ?? '';
              break;
            default:
              // Check if this is a seed field by key name
              const isSeedByName = key === 'seed' || key === 'seed_value' || 
                                 key.toLowerCase().includes('seed') ||
                                 mapping.title?.toLowerCase().includes('seed');
              if (isSeedByName) {
                const seedDefault = String(mapping.default ?? '0');
                defaults[key] = seedDefault.replace(/\D/g, '').slice(0, 15) || '0';
              } else {
                defaults[key] = mapping.default ?? '';
              }
          }
        });
      }
      return defaults;
    };

    const { handleSubmit, control, watch, setValue, getValues, reset } = useForm({
        defaultValues: getDefaultValues(),
        shouldUnregister: false,
        keepDefaultValues: true,
      });
    
    const [showAdvancedModal, setShowAdvancedModal] = useState(false);
    const [regenerateRandomSeeds, setRegenerateRandomSeeds] = useState(0);
    const [drawingInputOpen, setDrawingInputOpen] = useState(false);
    const [drawingInputImage, setDrawingInputImage] = useState(null);
    const [drawingInputFieldKey, setDrawingInputFieldKey] = useState(null);
    const [maskedFields, setMaskedFields] = useState({}); // Track which fields have masks
    const [maskOverlayPreviews, setMaskOverlayPreviews] = useState({}); // Store visual mask overlays for preview
  
    // Watch for prop changes and reset form when they change
    React.useEffect(() => {
      if (initialValues && Object.keys(initialValues).length > 0) {
        // Clean seed values in initialValues before resetting
        const cleanedValues = { ...initialValues };
        Object.entries(cleanedValues).forEach(([key, value]) => {
          // Check if this is a seed field by key name or schema
          const mapping = schema?.[key];
          const isSeedField = key === 'seed' ||
                            key === 'seed_value' ||
                            mapping?.type === 'seed' ||
                            mapping?.title?.toLowerCase().includes('seed') ||
                            key.toLowerCase().includes('seed');

          if (isSeedField && value) {
            // Clean the seed value - remove non-digits and limit to 15
            cleanedValues[key] = String(value).replace(/\D/g, '').slice(0, 15);
          }

          // Handle enum fields with numeric indices in initialValues
          if ((mapping?.type === 'enum' || mapping?.type === 'COMBO') && mapping?.options) {
            if (typeof value === 'number' && value >= 0 && value < mapping.options.length) {
              const originalValue = value;
              cleanedValues[key] = mapping.options[value]; // Convert index to string value
              // console.log(`[INIT VALUES] Converted enum field "${key}": index ${originalValue} → "${cleanedValues[key]}" (options: ${mapping.options.length})`);
            } else if (typeof value === 'string') {
              // Validate that the string value exists in options
              if (mapping.options.includes(value)) {
                // console.log(`[INIT VALUES] Enum field "${key}" valid string: "${value}"`);
              } else {
                // String value not in options, fallback to first option
                console.warn(`[INIT VALUES] Enum field "${key}" invalid string "${value}", using first option "${mapping.options[0]}"`);
                cleanedValues[key] = mapping.options[0];
              }
            } else if (value !== undefined) {
              console.warn(`[INIT VALUES] Invalid enum value for "${key}": ${value} (type: ${typeof value}), using first option "${mapping.options[0]}"`);
              cleanedValues[key] = mapping.options[0];
            }
          }
        });
        reset(cleanedValues);
      } else if (schema) {
        const defaults = {};
        Object.entries(schema).forEach(([key, mapping]) => {
          const type = mapping.type;
          switch (type) {
            case 'FLOAT':
              defaults[key] = mapping.default ?? mapping.min ?? 0;
              break;
            case 'INT':
            case 'number':
              defaults[key] = mapping.default ?? mapping.min ?? 1;
              break;
            case 'BOOLEAN':
            case 'boolean':
            case 'BOOL':
            case 'bool':
              // Ensure we always get a boolean value, defaulting to false
              if (mapping.default !== undefined) {
                defaults[key] = mapping.default === true || 
                               mapping.default === "true" || 
                               mapping.default === 1 || 
                               mapping.default === "1";
              } else {
                defaults[key] = false; // Explicitly default to false
              }
              break;
            case 'seed':
              // Ensure seed default is a string and limited to 15 digits
              const seedDefault = String(mapping.default ?? '0');
              defaults[key] = seedDefault.replace(/\D/g, '').slice(0, 15) || '0';
              break;
            case 'STRING':
            case 'prompt':
            case 'text':
            case 'url':
              defaults[key] = mapping.default ?? '';
              break;
            case 'enum':
            case 'COMBO':
              // Handle index-based defaults for enum fields
              if (mapping.default !== undefined && mapping.options) {
                if (typeof mapping.default === 'number' && mapping.default >= 0 && mapping.default < mapping.options.length) {
                  defaults[key] = mapping.options[mapping.default]; // Convert index to value
                  // console.log(`[ENUM DEFAULT] Converted index ${mapping.default} to value '${defaults[key]}' for field '${key}'`);
                } else if (typeof mapping.default === 'string') {
                  defaults[key] = mapping.default; // Use string default as-is
                  // console.log(`[ENUM DEFAULT] Using string default '${mapping.default}' for field '${key}'`);
                } else {
                  defaults[key] = mapping.options?.[0] || ''; // Fallback to first option
                  // console.log(`[ENUM DEFAULT] Invalid default (${mapping.default}), using first option '${defaults[key]}' for field '${key}'`);
                }
              } else {
                defaults[key] = mapping.options?.[0] || ''; // Fallback to first option
                // console.log(`[ENUM DEFAULT] No default specified, using first option '${defaults[key]}' for field '${key}'`);
              }
              break;
            case 'b64data':
            case 'IMAGE':  
              defaults[key] = mapping.default ?? '';
              break;
            default:
              // Check if this is a seed field by key name
              const isSeedByName = key === 'seed' || key === 'seed_value' || 
                                 key.toLowerCase().includes('seed') ||
                                 mapping.title?.toLowerCase().includes('seed');
              if (isSeedByName) {
                const seedDefault = String(mapping.default ?? '0');
                defaults[key] = seedDefault.replace(/\D/g, '').slice(0, 15) || '0';
              } else {
                defaults[key] = mapping.default ?? '';
              }
          }
        });
        reset(defaults);
      }
    }, [initialValues, schema, reset]);
    
    // Reset modal state when component unmounts or on navigation
    React.useEffect(() => {
      return () => {
        // Cleanup function - runs when component unmounts
        setShowAdvancedModal(false);
      };
    }, []);
    
    
    // Also watch for schema changes to ensure field definitions are updated
    React.useEffect(() => {
      // When schema changes significantly, reset form with current values
      if (schema && Object.keys(schema).length > 0) {
        const currentValues = getValues();
        reset(currentValues);
        // Close modal when model/schema changes
        setShowAdvancedModal(false);
      }
    }, [schema, reset, getValues]);
  
    // Expose form methods via ref
    useImperativeHandle(ref, () => ({
      submit: (callback) => handleSubmit(callback)(),  // wrap handleSubmit for external trigger
      getValues: () => {
        // Get all form values including dynamically set mask_image
        const values = getValues();
        const processedValues = { ...values };

        // Convert enum indices to string values for API submission
        if (schema) {
          for (const [fieldKey, mapping] of Object.entries(schema)) {
            if ((mapping.type === 'enum' || mapping.type === 'COMBO') && mapping.options) {
              const currentValue = processedValues[fieldKey];
              if (typeof currentValue === 'number' && currentValue >= 0 && currentValue < mapping.options.length) {
                const convertedValue = mapping.options[currentValue];
                processedValues[fieldKey] = convertedValue;
                // console.log(`[ENUM CONVERT] getValues field "${fieldKey}": Converted index ${currentValue} to value "${convertedValue}"`);
              } else if (typeof currentValue === 'string' && mapping.options.includes(currentValue)) {
                // console.log(`[ENUM CONVERT] getValues field "${fieldKey}": Valid string value "${currentValue}"`);
              } else if (currentValue !== undefined) {
                console.warn(`[ENUM CONVERT] getValues field "${fieldKey}": Invalid value ${currentValue}, using first option "${mapping.options[0]}"`);
                processedValues[fieldKey] = mapping.options[0];
              }
            }
          }
        }

        // console.log('[DynamicPromptForm] getValues returning:', Object.keys(processedValues));
        return processedValues;
      },
      validateAndGetValues: () => {
        // console.log('[DynamicPromptForm] validateAndGetValues: Starting pre-submission validation');

        // Get current form values
        const currentValues = getValues();
        const validatedValues = { ...currentValues };

        // Apply validation and conversion based on schema
        if (schema) {
          for (const [fieldKey, mapping] of Object.entries(schema)) {
            // Handle enum field conversion from index to value
            if ((mapping.type === 'enum' || mapping.type === 'COMBO') && mapping.options) {
              const currentValue = validatedValues[fieldKey];
              if (typeof currentValue === 'number' && currentValue >= 0 && currentValue < mapping.options.length) {
                const convertedValue = mapping.options[currentValue];
                validatedValues[fieldKey] = convertedValue;
                // console.log(`[ENUM CONVERT] Field "${fieldKey}": Converted index ${currentValue} to value "${convertedValue}"`);
              } else if (typeof currentValue === 'string' && mapping.options.includes(currentValue)) {
                // console.log(`[ENUM CONVERT] Field "${fieldKey}": Valid string value "${currentValue}"`);
              } else if (currentValue !== undefined) {
                console.warn(`[ENUM CONVERT] Field "${fieldKey}": Invalid value ${currentValue}, using first option "${mapping.options[0]}"`);
                validatedValues[fieldKey] = mapping.options[0];
              }
            }
            // Only validate numeric types that have constraints
            else if ((mapping.type === 'INT' || mapping.type === 'FLOAT') &&
                (mapping.min !== undefined || mapping.max !== undefined || mapping.step !== undefined)) {

              const currentValue = validatedValues[fieldKey];
              // console.log(`[VALIDATE] Field "${fieldKey}" current value:`, {
              //   currentValue,
              //   type: typeof currentValue,
              //   isEmpty: currentValue === undefined || currentValue === null || currentValue === '',
              //   mappingType: mapping.type
              // });

              // Handle both empty strings and valid numbers
              if (currentValue !== undefined && currentValue !== null) {
                // console.log(`[VALIDATE] Processing field "${fieldKey}": ${currentValue} (type: ${mapping.type})`);

                // Parse the current value - handle empty strings
                let numValue;
                if (currentValue === '') {
                  // Empty string should use minimum value
                  const minValue = mapping.min || (mapping.type === 'INT' ? 1 : 0);
                  numValue = minValue;
                  // console.log(`[VALIDATE] Field "${fieldKey}" was empty, using min value:`, minValue);
                } else {
                  numValue = mapping.type === 'INT' ? parseInt(currentValue, 10) : parseFloat(currentValue);
                }

                const minValue = mapping.min || (mapping.type === 'INT' ? 1 : 0);
                const maxValue = mapping.max;
                const stepValue = mapping.step || 1;

                // console.log(`[VALIDATE] Field "${fieldKey}" parsing:`, {
                //   originalValue: currentValue,
                //   parsedValue: numValue,
                //   isNaN: isNaN(numValue),
                //   minValue,
                //   maxValue,
                //   stepValue
                // });

                // Apply min/max constraints
                if (isNaN(numValue) || numValue < minValue) {
                  numValue = minValue;
                  // console.log(`[VALIDATE] Field "${fieldKey}": Applied min constraint ${minValue}`);
                } else if (maxValue !== undefined && numValue > maxValue) {
                  numValue = maxValue;
                  // console.log(`[VALIDATE] Field "${fieldKey}": Applied max constraint ${maxValue}`);
                }

                // Apply step-based divisibility validation
                if (stepValue && stepValue > 0) {
                  numValue = roundToNearestStep(numValue, minValue, stepValue);
                  // console.log(`[VALIDATE] Field "${fieldKey}": Applied step constraint ${stepValue}`);

                  // Ensure the step-adjusted value is still within bounds
                  if (numValue < minValue) {
                    numValue = minValue;
                  } else if (maxValue !== undefined && numValue > maxValue) {
                    // Find the largest step-divisible value within max bounds
                    const stepsFromMin = Math.floor((maxValue - minValue) / stepValue);
                    numValue = minValue + (stepsFromMin * stepValue);
                  }
                }

                // Update the validated value and form if it changed
                const originalNumValue = parseFloat(currentValue);
                const finalComparison = numValue !== originalNumValue;
                // console.log(`[VALIDATE] Field "${fieldKey}" final comparison:`, {
                //   numValue,
                //   currentValue,
                //   originalNumValue,
                //   needsUpdate: finalComparison,
                //   reason: finalComparison ? 'Constraint violation detected' : 'Value within constraints'
                // });

                if (finalComparison) {
                  // console.log(`[VALIDATE] Field "${fieldKey}": UPDATING form and validated values from ${currentValue} to ${numValue} (constraint applied)`);
                  validatedValues[fieldKey] = numValue;
                  // Only update form if the value is actually invalid according to constraints
                  // This prevents overriding user input that's already valid
                  setValue(fieldKey, numValue);
                } else {
                  // console.log(`[VALIDATE] Field "${fieldKey}": No change needed - value ${currentValue} is within constraints`);
                  validatedValues[fieldKey] = originalNumValue; // Use parsed version for consistency
                }
              }
            }
          }
        }

        // console.log('[DynamicPromptForm] validateAndGetValues: Validation complete');
        // console.log('[DynamicPromptForm] Final validated values:', validatedValues);

        // Log detailed comparison between original and final values
        // console.log('[DynamicPromptForm] Value comparison summary:');
        // Object.keys(validatedValues).forEach(key => {
        //   const originalValue = currentValues[key];
        //   const finalValue = validatedValues[key];
        //   if (originalValue !== finalValue) {
        //     console.log(`  ${key}: ${originalValue} → ${finalValue} (${typeof originalValue} → ${typeof finalValue})`);
        //   }
        // });

        return validatedValues;
      },
      setValue,  // Expose setValue to allow external updates
      resetForm: (newDefaults) => {
        reset(newDefaults);
        // Close modal when form is reset
        setShowAdvancedModal(false);
      },
      regenerateRandomSeeds: () => {
        // Increment the regenerate key to trigger new random seeds
        setRegenerateRandomSeeds(prev => prev + 1);
      },
    }));

  const renderInput = (key, mapping) => {
    const type = mapping.type;    

    if (key === 'cosyflow') return null;
    
    // Hide mask input fields - they'll be handled through the input image editor
    const maskFields = findMaskFields(schema || {});
    if (maskFields.includes(key)) {
      // console.log(`[DynamicPromptForm] Hiding mask field: ${key}`);
      return null;
    }

    const label = formatLabelText(mapping.title);
    const tooltip = mapping.tooltip ? <img src={InfoIcon} title={mapping.tooltip} className="h-6" /> : null;
    
    // Debug logging for seed fields
    // if (key === 'seed' || label.toLowerCase().includes('seed')) {
    //   console.log('[DynamicPromptForm] Seed field detected:', {
    //     key,
    //     type,
    //     label,
    //     title: mapping.title,
    //     mapping
    //   });
    // }

    switch (type) {
      case 'seed':
        const seedMode = watch(`${key}_mode`) || 'random';
        return (
          <>
            <Controller 
              name={`${key}_mode`}
              control={control}
              defaultValue="random"
              render={({ field: modeField }) => (
                <div className="mb-2">
                  <label className="w-full flex flex-row gap-3 items-center mb-3">
                    <span className="text-yellow font-medium text-base">{label}</span>
                    {tooltip && <span className="opacity-70 text-sm">{tooltip}</span>}
                    <select
                      {...modeField}
                      className="ml-auto px-3 py-1 rounded-lg bg-dark-purple/40 border border-white/20 text-white text-sm focus:border-yellow focus:ring-1 focus:ring-yellow/50 transition-all"
                    >
                      <option value="fixed">Fixed</option>
                      <option value="increment">Increment</option>
                      <option value="decrement">Decrement</option>
                      <option value="random">Random</option>
                    </select>
                  </label>
                </div>
              )}
            />
            <Controller
              name={key}
              control={control}
              render={({ field }) => <SeedField field={field} mode={seedMode} seedKey={key} onFieldChange={onFieldChange} onFetchLatestSeed={onFetchLatestSeed} fieldType={type} />}
            />
          </>
        );
      case 'FLOAT':
        return (
          <Controller
            name={key}
            control={control}
            render={({ field }) => (
              <div className="mb-4">
                <label className="w-full flex flex-row gap-3 items-center mb-3">
                  <span className="text-yellow font-medium text-base">{label}</span>
                  {tooltip && <span className="opacity-70 text-sm">{tooltip}</span>}
                  <span className="ml-auto bg-yellow/20 text-yellow px-2 py-1 rounded text-xs font-mono min-w-[60px] text-center">
                    {field.value ?? 0}
                  </span>
                </label>
                <input
                  type="range"
                  min={mapping.min || 0}
                  max={mapping.max || 20}
                  step={mapping.step || 0.1}
                  className="w-full h-3 bg-white/20 rounded-lg appearance-none cursor-pointer slider-input hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-yellow/50 transition-colors"
                  style={{
                    background: `linear-gradient(to right, #f6bb08 0%, #f6bb08 ${(((field.value ?? 0) - (mapping.min || 0)) / ((mapping.max || 20) - (mapping.min || 0))) * 100}%, rgba(255, 255, 255, 0.2) ${(((field.value ?? 0) - (mapping.min || 0)) / ((mapping.max || 20) - (mapping.min || 0))) * 100}%, rgba(255, 255, 255, 0.2) 100%)`
                  }}
                  {...field}
                  value={field.value ?? 0}
                  onChange={(e) => {
                    const numValue = parseFloat(e.target.value);
                    field.onChange(numValue);
                    if (onFieldChange) {
                      onFieldChange(key, numValue);
                    }
                  }}
                />
              </div>
            )}
          />
        );
      case 'INT':
      case 'number':
        return (
          <Controller
            name={key}
            control={control}
            render={({ field }) => (
              <div className="mb-4">
                <label className="w-full flex flex-row gap-3 items-center mb-3">
                  <span className="text-yellow font-medium text-base">{label}</span>
                  {tooltip && <span className="opacity-70 text-sm">{tooltip}</span>}
                </label>
                <input 
                  type="number" 
                  min={mapping.min || 1}
                  max={mapping.max}
                  step={mapping.step || 1}
                  className="w-full p-3 rounded-lg bg-dark-purple/40 border border-white/20 text-white text-base placeholder-white/50 focus:border-yellow focus:ring-1 focus:ring-yellow/50 focus:bg-dark-purple/50 transition-all" 
                  placeholder={`Enter ${label.toLowerCase()}`}
                  {...field}
                  value={field.value ?? 1}
                  onChange={(e) => {
                    // let numValue = e.target.value === '' ? 1 : parseInt(e.target.value, 10);
                    
                    // // Get min and max constraints
                    // const minValue = mapping.min || 1;
                    // const maxValue = mapping.max;
                    
                    // // Enforce minimum constraint
                    // if (numValue < minValue) {
                    //   numValue = minValue;
                    // }
                    
                    // // Enforce maximum constraint if it exists
                    // if (maxValue !== undefined && numValue > maxValue) {
                    //   numValue = maxValue;
                    // }
                    
                    // const finalValue = isNaN(numValue) ? minValue : numValue;
                    // field.onChange(finalValue);
                    // if (onFieldChange) {
                    //   onFieldChange(key, finalValue);
                    // }
                    // Parse value as integer for INT fields, store as number not string
                    const rawValue = e.target.value;

                    // console.log(`[NUMBER INPUT CHANGE] Field "${key}":`, {
                    //     rawValue,
                    //     rawValueLength: rawValue.length,
                    //     isEmpty: rawValue === ''
                    // });

                    // Handle empty input - use minimum value or 0 instead of empty string
                    let formValue;
                    let indexedDBValue;

                    if (rawValue === '') {
                        // For empty input, use minimum value or 1 for INT, 0 for others
                        const minValue = mapping.min || (mapping.type === 'INT' ? 1 : 0);
                        formValue = minValue;
                        indexedDBValue = minValue;
                        // console.log(`[NUMBER INPUT] Field "${key}" empty input, using min value:`, minValue);
                    } else {
                        // Parse the value
                        const numValue = parseInt(rawValue, 10);

                        if (isNaN(numValue)) {
                            // Invalid input - revert to minimum value
                            const minValue = mapping.min || (mapping.type === 'INT' ? 1 : 0);
                            formValue = minValue;
                            indexedDBValue = minValue;
                            // console.log(`[NUMBER INPUT] Field "${key}" invalid input, using min value:`, minValue);
                        } else {
                            // Valid number - use as is
                            formValue = numValue;
                            indexedDBValue = numValue;
                            // console.log(`[NUMBER INPUT] Field "${key}" valid input:`, numValue);
                        }
                    }

                    // Store the value in the form (always a number, never empty string)
                    field.onChange(formValue);
                    // console.log(`[NUMBER INPUT] Field "${key}" form value set to:`, formValue, typeof formValue);

                    // Always save to IndexedDB since we always have a valid number
                    if (onFieldChange) {
                        // console.log(`[NUMBER INPUT] Field "${key}" saving to IndexedDB:`, indexedDBValue);
                        onFieldChange(key, indexedDBValue);
                    }
                  }}
                  onBlur={(e) => {
                    // // Enforce min/max constraints on blur as well
                    // let currentValue = parseInt(e.target.value, 10);
                    // const minValue = mapping.min || 1;
                    // const maxValue = mapping.max;
                    
                    // // Check if value is NaN or below minimum
                    // if (isNaN(currentValue) || currentValue < minValue) {
                    //   field.onChange(minValue);
                    //   if (onFieldChange) {
                    //     onFieldChange(key, minValue);
                    //   }
                    // }
                    // // Check if value exceeds maximum
                    // else if (maxValue !== undefined && currentValue > maxValue) {
                    //   field.onChange(maxValue);
                    //   if (onFieldChange) {
                    //     onFieldChange(key, maxValue);
                    //   }
                    // }
                    // Enforce constraints when user finishes editing
                    let numValue = parseInt(e.target.value, 10);
                    const minValue = mapping.min || 1;
                    const maxValue = mapping.max;
                    const stepValue = mapping.step || 1;

                    if (isNaN(numValue) || numValue < minValue) {
                        numValue = minValue;
                    } else if (maxValue !== undefined && numValue > maxValue) {
                        numValue = maxValue;
                    }

                    // Apply step-based divisibility validation
                    numValue = roundToNearestStep(numValue, minValue, stepValue);

                    // Ensure the step-adjusted value is still within bounds
                    if (numValue < minValue) {
                        numValue = minValue;
                    } else if (maxValue !== undefined && numValue > maxValue) {
                        // Find the largest step-divisible value within max bounds
                        const stepsFromMin = Math.floor((maxValue - minValue) / stepValue);
                        numValue = minValue + (stepsFromMin * stepValue);
                    }

                    field.onChange(numValue);
                    if (onFieldChange) {
                        onFieldChange(key, numValue);
                    }
                  }}
                />
              </div>
            )}
          />
        );
      case 'STRING':
        return (
          <Controller
            name={key}
            control={control}
            render={({ field }) => (
              <StringTextarea
                field={field}
                label={label}
                tooltip={tooltip}
                fieldKey={key}
                onFieldChange={onFieldChange}
                maxLength={mapping.max_length}
              />
            )}
          />
        );
      case 'prompt':
        // Special handling for prompt fields with enhanced features
        return (
          <Controller
            name={key}
            control={control}
            render={({ field }) => (
              <PromptTextarea
                field={field}
                label={label}
                tooltip={tooltip}
                maxLength={mapping.max_length}
              />
            )}
          />
        );
      case 'enum':
      case 'COMBO':
        return (
          <Controller
            name={key}
            control={control}
            render={({ field }) => {
              // Enhanced debug logging for enum fields
              if (mapping.type === 'enum' || mapping.type === 'COMBO') {
                // console.log(`[ENUM RENDER] Field "${key}":`, {
                //   fieldValue: field.value,
                //   fieldValueType: typeof field.value,
                //   mappingDefault: mapping.default,
                //   mappingDefaultType: typeof mapping.default,
                //   mappingOptions: mapping.options,
                //   optionsLength: mapping.options?.length,
                //   isValidIndex: typeof field.value === 'number' && field.value >= 0 && field.value < mapping.options?.length,
                //   isValidString: typeof field.value === 'string' && mapping.options?.includes(field.value),
                //   firstOption: mapping.options?.[0],
                //   selectedOption: typeof field.value === 'number' ? mapping.options?.[field.value] : field.value
                // });

                // Check if field.value is still a numeric index and needs conversion
                if (typeof field.value === 'number' && field.value >= 0 && field.value < mapping.options?.length) {
                  console.warn(`[ENUM RENDER] Field "${key}" has numeric value ${field.value}, converting to string "${mapping.options[field.value]}"`);
                  field.onChange(mapping.options[field.value]);
                }
              }
              
              return (
                <div className="mb-4">
                  <label className="w-full flex flex-row gap-3 items-center mb-3">
                    <span className="text-yellow font-medium text-base">{label}</span>
                    {tooltip && <span className="opacity-70 text-sm">{tooltip}</span>}
                  </label>
                  <select
                    className="w-full p-3 rounded-lg bg-dark-purple/40 border border-white/20 text-white focus:border-yellow focus:ring-1 focus:ring-yellow/50 focus:bg-dark-purple/50 transition-all"
                    {...field}
                    value={(() => {
                      // Ensure we always have a valid string value for the select
                      if (typeof field.value === 'number' && field.value >= 0 && field.value < mapping.options?.length) {
                        return mapping.options[field.value];
                      }
                      if (typeof field.value === 'string' && mapping.options?.includes(field.value)) {
                        return field.value;
                      }
                      // Fallback to first option if value is invalid
                      return mapping.options?.[0] || '';
                    })()}
                    onChange={(e) => {
                      // console.log(`[ENUM CHANGE] Field "${key}": "${e.target.value}"`);
                      field.onChange(e.target.value);
                      if (onFieldChange) {
                        onFieldChange(key, e.target.value);
                      }
                    }}>
                    {mapping.options && mapping.options.map((option, index) => {
                      // if (key === 'scheduler') {
                      //   console.log(`[DynamicPromptForm] Rendering scheduler option ${index}:`, option);
                      // }
                      return (
                        <option key={option} value={option} className="bg-dark-blue text-white">{option}</option>
                      );
                    })}
                  </select>
                </div>
              );
            }}
          />
        );
      case 'b64data':
      case 'IMAGE':  
        return (
          <Controller
            name={key}
            control={control}
            render={({ field }) => {
              const hasValue = watch(key);
              
              // Check for upload type flags in the mapping
              const supportsImage = mapping.image_upload === true;
              const supportsAudio = mapping.audio_upload === true;
              const supportsVideo = mapping.video_upload === true;
              
              // Fallback to label-based detection if no flags specified
              const isImageField = !supportsImage && !supportsAudio && !supportsVideo && 
                                   (label.toLowerCase().includes('image') || label.toLowerCase().includes('img'));
              const isAudioField = !supportsImage && !supportsAudio && !supportsVideo && 
                                   (label.toLowerCase().includes('audio') || label.toLowerCase().includes('sound'));
              const isVideoField = !supportsImage && !supportsAudio && !supportsVideo &&
                                   (label.toLowerCase().includes('video') || label.toLowerCase().includes('vid'));
              
              // Build accept attribute based on flags or fallback detection
              let acceptTypes = [];
              if (supportsImage) {
                acceptTypes.push('image/*');
              } else if (supportsAudio) {
                acceptTypes.push('audio/*');
              } else if (supportsVideo) {
                acceptTypes.push('video/*');
              } else if (isImageField) {
                acceptTypes.push('image/*');
              } else if (isAudioField) {
                acceptTypes.push('audio/*');
              } else if (isVideoField) {
                acceptTypes.push('video/*');
              } else {
                // Default to image and audio if nothing specified
                acceptTypes = ['image/*', 'audio/*'];
              }
              
              // Allow multiple types if multiple flags are true
              if (supportsImage && supportsAudio) acceptTypes = ['image/*', 'audio/*'];
              if (supportsImage && supportsVideo) acceptTypes = ['image/*', 'video/*'];
              if (supportsAudio && supportsVideo) acceptTypes = ['audio/*', 'video/*'];
              if (supportsImage && supportsAudio && supportsVideo) acceptTypes = ['image/*', 'audio/*', 'video/*'];
              
              const acceptAttribute = acceptTypes.join(',');
              
              // Determine icon to show
              const getIcon = () => {
                if (supportsVideo || isVideoField) return '🎬';
                if (supportsAudio || isAudioField) return '🎵';
                if (supportsImage || isImageField) return '🖼️';
                return '📁';
              };
              
              // Build drop text based on supported types
              const getDropText = () => {
                const types = [];
                if (acceptTypes.includes('image/*')) types.push('image');
                if (acceptTypes.includes('audio/*')) types.push('audio');
                if (acceptTypes.includes('video/*')) types.push('video');
                
                if (types.length === 1) {
                  return `Drop ${types[0]} file here or click to browse`;
                } else if (types.length > 1) {
                  return `Drop ${types.join(' or ')} file here or click to browse`;
                }
                return 'Drop file here or click to browse';
              };
              
              // Build help text based on supported types
              const getHelpText = () => {
                const formats = [];
                if (acceptTypes.includes('image/*')) formats.push('Images (JPG, PNG, GIF, WebP)');
                if (acceptTypes.includes('audio/*')) formats.push('Audio (MP3, WAV, OGG, M4A)');
                if (acceptTypes.includes('video/*')) formats.push('Video (MP4, WebM, MOV, AVI)');
                
                return formats.length > 0 ? `Supports: ${formats.join(', ')}` : 'Supports: Various file formats';
              };
              
              return (
                <div className="mb-4">
                  <label className="w-full flex flex-row gap-3 items-center mb-3">
                    <span className="text-yellow font-medium text-base">{label}</span>
                    {tooltip && <span className="opacity-70 text-sm">{tooltip}</span>}
                  </label>
                  <div className="border-2 border-dashed border-white/20 rounded-lg p-6 hover:border-yellow/50 transition-all bg-dark-purple/20 hover:bg-dark-purple/30 relative">
                    {!hasValue ? (
                      <>
                        <div className="text-center mb-4">
                          <div className="flex justify-center mb-3">
                            <span className="text-4xl opacity-50">
                              {getIcon()}
                            </span>
                          </div>
                          <p className="text-white/70 text-base font-medium mb-1">
                            {getDropText()}
                          </p>
                          <p className="text-white/40 text-sm">
                            {getHelpText()}
                          </p>
                        </div>
                        <input 
                          type="file" 
                          onChange={(e) => handleFileUpload(e, key, setValue)}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                          accept={acceptAttribute}
                        />
                      </>
                    ) : (
                      <>
                        <MediaPreviewComponent
                          data={watch(key)}
                          fieldLabel={label}
                          mapping={mapping}
                          fieldKey={key}
                        />
                        <div className="mt-4">
                          <input 
                            type="file" 
                            onChange={(e) => handleFileUpload(e, key, setValue)}
                            className="w-full text-white/70 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-yellow file:text-dark-blue file:font-medium hover:file:bg-yellow/90 file:cursor-pointer"
                            accept="audio/*,image/*"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            }}
          />
        );
      
      case 'BOOLEAN':
      case 'boolean':
      case 'BOOL':
      case 'bool':
        return (
          <Controller
            key={key}
            name={key}
            control={control}
            defaultValue={mapping.default === true || mapping.default === "true" || mapping.default === 1}
            render={({ field }) => (
              <div className="flex items-center space-x-3 mb-4">
                <ToggleInput
                  id={key}
                  checked={field.value === true || field.value === "true" || field.value === 1}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    field.onChange(newValue);
                    if (onFieldChange) {
                      onFieldChange(key, newValue);
                    }
                  }}
                />
                <label htmlFor={key} className={`text-sm font-medium ${field.value ? 'text-yellow' : 'text-gray-400'} transition`}>
                  {label}
                </label>
                {mapping.tooltip && (
                  <img src={InfoIcon} title={mapping.tooltip} className="h-5 w-5" alt="info" />
                )}
              </div>
            )}
          />
        );
      
      default:
        // Only fields with type 'seed' get seed functionality
        // As per reviewer feedback: type 'seed' is distinct and cannot be type 'STRING'
        if (mapping.type === 'seed') {
          const defaultSeedMode = watch(`${key}_mode`) || 'random';
          return (
            <>
              <Controller
                name={`${key}_mode`}
                control={control}
                defaultValue="random"
                render={({ field: modeField }) => (
                  <div className="mb-2">
                    <label className="w-full flex flex-row gap-3 items-center mb-3">
                      <span className="text-yellow font-medium text-base">{label}</span>
                      {tooltip && <span className="opacity-70 text-sm">{tooltip}</span>}
                      <select
                        {...modeField}
                        className="ml-auto px-3 py-1 rounded-lg bg-dark-purple/40 border border-white/20 text-white text-sm focus:border-yellow focus:ring-1 focus:ring-yellow/50 transition-all"
                      >
                        <option value="fixed">Fixed</option>
                        <option value="increment">Increment</option>
                        <option value="decrement">Decrement</option>
                        <option value="random">Random</option>
                      </select>
                    </label>
                  </div>
                )}
              />
              <Controller
                name={key}
                control={control}
                render={({ field }) => <SeedField field={field} mode={defaultSeedMode} seedKey={key} onFieldChange={onFieldChange} onFetchLatestSeed={onFetchLatestSeed} fieldType={mapping.type} />}
              />
            </>
          );
        }
        
        // Default text input for other fields
        return (
          <Controller
            name={key}
            control={control}
            render={({ field }) => {
              return (
                <div className="mb-4">
                  <label className="w-full flex flex-row gap-3 items-center mb-3">
                    <span className="text-yellow font-medium text-base">{label}</span>
                    {tooltip && <span className="opacity-70 text-sm">{tooltip}</span>}
                  </label>
                  <input 
                    type="text" 
                    className="w-full p-3 rounded-lg bg-dark-purple/40 border border-white/20 text-white text-base placeholder-white/50 focus:border-yellow focus:ring-1 focus:ring-yellow/50 focus:bg-dark-purple/50 transition-all" 
                    placeholder={`Enter ${label.toLowerCase()}`}
                    name={key}
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      if (onFieldChange) {
                        onFieldChange(key, e.target.value);
                      }
                    }}
                  />
                </div>
              );
            }}
          />
        );

      case 'url':
        return (
          <Controller
            name={key}
            control={control}
            render={({ field }) => (
              <URLInputComponent
                field={field}
                label={label}
                tooltip={tooltip}
                onFieldChange={onFieldChange}
                fieldKey={key}
              />
            )}
          />
        );
    }
  };

  const handleFileUpload = (e, key, setValue) => {
    const file = e.target.files[0];
    
    // If no file (user cleared the input), clear both the field and any associated mask
    if (!file) {
      setValue(key, '');
      
      // Clear any associated mask fields using schema properties
      const currentSchema = schema || {};
      const maskFields = findMaskFields(currentSchema);
      for (const maskFieldKey of maskFields) {
        setValue(maskFieldKey, '');
        if (onFieldChange) onFieldChange(maskFieldKey, '');
        if (onB64Upload) onB64Upload(maskFieldKey, '');
      }
      
      // Clear masked field tracking
      setMaskedFields(prev => {
        const newMaskedFields = { ...prev };
        delete newMaskedFields[key];
        return newMaskedFields;
      });
      
      // Clear mask overlay preview
      setMaskOverlayPreviews(prev => {
        const newPreviews = { ...prev };
        delete newPreviews[key];
        return newPreviews;
      });
      
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      let base64 = reader.result;
      base64 = base64.replace(/^data:(.*?);base64,/, '');
      setValue(key, base64);
      
      // When a new image is uploaded, clear any existing mask
      const currentSchema = schema || {};
      const maskFields = findMaskFields(currentSchema);
      for (const maskFieldKey of maskFields) {
        setValue(maskFieldKey, '');
        if (onFieldChange) onFieldChange(maskFieldKey, '');
        if (onB64Upload) onB64Upload(maskFieldKey, '');
      }
      
      // Clear the masked field indicator when a new file is uploaded
      setMaskedFields(prev => {
        const newMaskedFields = { ...prev };
        delete newMaskedFields[key];
        return newMaskedFields;
      });
      
      // Clear mask overlay preview when new image is uploaded
      setMaskOverlayPreviews(prev => {
        const newPreviews = { ...prev };
        delete newPreviews[key];
        return newPreviews;
      });
      
      // Call parent callback to persist the uploaded data
      if (onB64Upload) {
        onB64Upload(key, base64);
      }
    };
    reader.readAsDataURL(file);
  };

  // Async Media Preview Component
  const MediaPreviewComponent = ({ data, fieldLabel, mapping, fieldKey }) => {
    const [preview, setPreview] = useState(null);

    useEffect(() => {
      const generatePreview = async () => {
        const previewContent = await renderPreview(data, fieldLabel, mapping, fieldKey);
        setPreview(previewContent);
      };

      if (data) {
        generatePreview();
      } else {
        setPreview(null);
      }
    }, [data, fieldLabel, mapping, fieldKey]);

    return preview;
  };

  const renderPreview = async (data, fieldLabel, mapping, fieldKey) => {
    if (!data) return null;

    // Check if this field allows URLs
    const urlAllowed = mapping?.url_allowed === true;

    // Check if data is a URL
    const isUrl = (str) => {
      try {
        new URL(str);
        return true;
      } catch {
        return false;
      }
    };

    // Check if data is actually valid base64 (not just a filename)
    const isValidBase64 = (str) => {
      // Check if it's a filename pattern (ends with common extensions)
      if (/\.(png|jpg|jpeg|gif|bmp|webp|mp3|wav|ogg|m4a|mp4|webm|mov|avi)$/i.test(str)) {
        return false;
      }
      // Basic check for base64 string - check if it has base64 characters
      // Don't check for = in the beginning as padding only appears at the end
      return str.length > 50 && /^[A-Za-z0-9+/]+/.test(str.substring(0, 100));
    };

    // Check if data is valid (base64 or URL if allowed)
    const isValidData = isValidBase64(data) || (urlAllowed && isUrl(data));

    // If data is not valid, don't show preview
    if (!isValidData) {
      console.warn('Invalid data for preview:', data.substring(0, 50) + '...');
      return null;
    }
    
    // Check for upload type flags in mapping
    const supportsImage = mapping?.image_upload === true;
    const supportsAudio = mapping?.audio_upload === true;
    const supportsVideo = mapping?.video_upload === true;
    
    // Check if this is an image based on common base64 prefixes
    const imagePatterns = {
      '/9j': 'JPEG',
      'iVBOR': 'PNG',
      'R0lGOD': 'GIF',
      'UklGR': 'WEBP',
      'Qk': 'BMP',
      'SUkq': 'TIFF',
      'II': 'TIFF',
      'MM': 'TIFF'
    };
    
    // Check for audio patterns (common audio file signatures)
    const audioPatterns = {
      'ID3': 'MP3',      // MP3 files with ID3 tags
      '//OgA': 'OGA',    // OGG audio files
      'OggS': 'OGG',     // OGG container format
      'fLaC': 'FLAC',    // FLAC files
      'RIFF': 'WAV',     // WAV files (will need additional checks)
      'ftypM4A ': 'M4A', // M4A audio files (note the space)
      'ftypM4A': 'M4A',  // M4A audio files (without space)
      'mp4a': 'AAC'      // AAC audio codec marker
    };

    // Check for video patterns (common video file signatures)
    const videoPatterns = {
      'ftypmp41': 'MP4', // Specific MP4 video signature
      'ftypmp42': 'MP4', // MP4 video container (shared with audio, handle carefully)
      'ftypisom': 'MP4', // ISO base media file format (MP4)
      'GkX': 'WebM',     // WebM signature
      'ftypqt': 'MOV',   // QuickTime MOV
      'Rk': 'AVI'        // AVI RIFF header
    };

    let isImage = false;
    let imageFormat = 'Unknown';
    let isAudio = false;
    let audioFormat = 'Unknown';
    let isVideo = false;
    let videoFormat = 'Unknown';
    
    // Check for image patterns first
    for (const [prefix, format] of Object.entries(imagePatterns)) {
      if (data.startsWith(prefix)) {
        isImage = true;
        imageFormat = format;
        break;
      }
    }

    // Check for audio patterns (only if not an image)
    if (!isImage) {
      for (const [prefix, format] of Object.entries(audioPatterns)) {
        if (data.includes(prefix)) {
          // Special handling for RIFF format to differentiate WAV from AVI
          if (prefix === 'RIFF') {
            // Check if it's specifically a WAVE file
            if (data.includes('WAVE')) {
              isAudio = true;
              audioFormat = 'WAV';
            }
          } else {
            isAudio = true;
            audioFormat = format;
          }
          break;
        }
      }
    }

    // Check for video patterns (only if not image or audio)
    if (!isImage && !isAudio) {
      for (const [prefix, format] of Object.entries(videoPatterns)) {
        if (data.includes(prefix)) {
          // Handle the shared ftypmp42 signature carefully
          if (prefix === 'ftypmp42') {
            // Check for audio-specific indicators first
            if (data.includes('M4A') || data.includes('mp4a') || data.includes('soun')) {
              isAudio = true;
              audioFormat = 'M4A';
            } else if (data.includes('avc1') || data.includes('mp4v') || data.includes('vide')) {
              // Check for video-specific indicators
              isVideo = true;
              videoFormat = format;
            } else {
              // Default to video for ftypmp42 if no specific indicators found
              isVideo = true;
              videoFormat = format;
            }
          } else {
            isVideo = true;
            videoFormat = format;
          }
          break;
        }
      }
    }
    
    // Also check if the field label indicates the type
    const isImageField = fieldLabel && (
      fieldLabel.toLowerCase().includes('image') || 
      fieldLabel.toLowerCase().includes('img') ||
      fieldLabel.toLowerCase().includes('picture') ||
      fieldLabel.toLowerCase().includes('photo')
    );
    
    const isVideoField = fieldLabel && (
      fieldLabel.toLowerCase().includes('video') ||
      fieldLabel.toLowerCase().includes('vid') ||
      fieldLabel.toLowerCase().includes('movie') ||
      fieldLabel.toLowerCase().includes('clip')
    );
    
    const isAudioField = fieldLabel && (
      fieldLabel.toLowerCase().includes('audio') ||
      fieldLabel.toLowerCase().includes('sound') ||
      fieldLabel.toLowerCase().includes('music')
    );
    
    // Determine what to show - PRIORITIZE EXPLICIT FLAGS FIRST
    // If field explicitly specifies a type, trust it completely
    if (supportsVideo) {
      return (
        <div className="mt-3 text-center">
          <div className="inline-block relative group">
            <Video
              image={`${data}`}
              className="w-[300px] h-auto rounded-lg border-2 border-yellow/30 shadow-lg"
              controls
              hideToolbar={true}
            />
            <div className="absolute top-2 right-2 bg-dark-blue/80 px-2 py-1 rounded text-xs text-yellow flex items-center gap-1">
              <span>🎬</span>
              <span>Video</span>
            </div>
          </div>
          <p className="text-white/60 text-sm mt-2">Video uploaded successfully</p>
        </div>
      );
    }

    if (supportsAudio) {
      return (
        <div className="mt-3 text-center">
          <div className="inline-block relative group">
            <div className="bg-dark-purple/40 p-4 rounded-lg border border-yellow/30">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">🎵</span>
                <span className="text-yellow font-medium">Audio File</span>
              </div>
              <audio
                src={urlAllowed && isUrl(data) ? data : `data:audio/mpeg;base64,${data}`}
                controls
                className="max-w-full"
              />
            </div>
            <div className="absolute top-2 right-2 bg-dark-blue/80 px-2 py-1 rounded text-xs text-yellow flex items-center gap-1">
              <span>🎵</span>
              <span>Audio</span>
            </div>
            <p className="text-white/60 text-sm mt-2">Audio uploaded successfully</p>
          </div>
        </div>
      );
    }

    if (supportsImage) {
      // Use mask overlay preview if available, otherwise use original image
      const displayImage = maskOverlayPreviews[fieldKey] || data;
      const isMaskOverlay = !!maskOverlayPreviews[fieldKey];
      const modelRequiresMask = checkIfModelRequiresMask(schema);

      // Helper function to determine image source
      const getImageSrc = (imageData) => {
        // If it's a file path (contains :\)
        if (imageData && imageData.includes(":\\")) {
          return toFileUrl(imageData);
        }
        // If it's a URL and URL is allowed for this field
        if (urlAllowed && isUrl(imageData)) {
          return imageData;
        }
        // Otherwise treat as base64
        return `data:image/*;base64,${imageData}`;
      };

      return (
        <div className="mt-3 text-center">
          <div className="inline-block relative group">
            <img
              src={getImageSrc(displayImage)}
              alt="preview"
              className={`w-[250px] h-[250px] object-cover rounded-lg shadow-lg transition-all ${
                modelRequiresMask ? 'cursor-pointer' : 'cursor-default'
              } ${
                isMaskOverlay
                  ? 'border-2 border-purple-500 hover:border-purple-400'
                  : modelRequiresMask
                    ? 'border-2 border-yellow/30 hover:border-yellow/50'
                    : 'border-2 border-gray/30'
              }`}
              onClick={() => {
                if (modelRequiresMask) {
                  setDrawingInputImage(data);
                  setDrawingInputFieldKey(fieldKey);
                  setDrawingInputOpen(true);
                }
              }}
            />
            {modelRequiresMask && (
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center pointer-events-none">
                <div className="text-white text-center">
                  <div className="text-2xl mb-2">✏️</div>
                  <div className="text-sm font-medium">
                    {isMaskOverlay ? 'Click to edit mask' : 'Click to add mask'}
                  </div>
                </div>
              </div>
            )}
            <div className="absolute top-2 right-2 bg-dark-blue/80 px-2 py-1 rounded text-xs text-yellow flex items-center gap-1">
              <span>🖼️</span>
              <span>Image</span>
              {isMaskOverlay && modelRequiresMask && (
                <span className="ml-1 bg-purple-600 text-white px-1 rounded flex items-center gap-1">
                  <span>🎭</span> MASKED
                </span>
              )}
            </div>
            {isMaskOverlay && modelRequiresMask && (
              <div className="absolute bottom-2 left-2 right-2 bg-purple-900/80 text-white text-xs px-2 py-1 rounded">
                Purple areas will be regenerated
              </div>
            )}
          </div>
          <p className="text-white/60 text-sm mt-2">
            {modelRequiresMask ? (
              isMaskOverlay
                ? "✅ Mask applied - Purple areas show where changes will occur"
                : "📸 Image uploaded - Click to add mask for inpainting"
            ) : (
              "📸 Image uploaded successfully"
            )}
          </p>
        </div>
      );
    }

    // ENHANCED FALLBACK: No explicit flags - use getMediaType, detection and field labels
    // Use getMediaType as additional fallback when no flags present and detection is uncertain
    let detectedType = null;
    if (!supportsImage && !supportsAudio && !supportsVideo) {
      try {
        detectedType = await getMediaType(data);
        // console.log('getMediaType detected:', detectedType);
      } catch (error) {
        console.warn('getMediaType failed, using original detection:', error);
      }
    }

    const shouldShowImage = isImage || isImageField || (detectedType === 'Image');
    const shouldShowAudio = (isAudio || isAudioField || (detectedType === 'Audio')) && !shouldShowImage;
    const shouldShowVideo = (isVideo || isVideoField || (detectedType === 'Video')) && !shouldShowImage && !shouldShowAudio;
    
    // Image preview
    if (shouldShowImage) {
      // Use mask overlay preview if available, otherwise use original image
      const displayImage = maskOverlayPreviews[fieldKey] || data;
      const isMaskOverlay = !!maskOverlayPreviews[fieldKey];
      const modelRequiresMask = checkIfModelRequiresMask(schema);

      // Helper function to determine image source
      const getImageSrc = (imageData) => {
        // If it's a file path (contains :\)
        if (imageData && imageData.includes(":\\")) {
          return toFileUrl(imageData);
        }
        // If it's a URL and URL is allowed for this field
        if (urlAllowed && isUrl(imageData)) {
          return imageData;
        }
        // Otherwise treat as base64
        return `data:image/*;base64,${imageData}`;
      };

      return (
        <div className="mt-3 text-center">
          <div className="inline-block relative group">
            <img
              src={getImageSrc(displayImage)}
              alt="preview"
              className={`w-[250px] h-[250px] object-cover rounded-lg shadow-lg transition-all ${
                modelRequiresMask ? 'cursor-pointer' : 'cursor-default'
              } ${
                isMaskOverlay
                  ? 'border-2 border-purple-500 hover:border-purple-400'
                  : modelRequiresMask
                    ? 'border-2 border-yellow/30 hover:border-yellow/50'
                    : 'border-2 border-gray/30'
              }`}
              onClick={() => {
                if (modelRequiresMask) {
                  setDrawingInputImage(data); // Always use original image for editing
                  setDrawingInputFieldKey(fieldKey);
                  setDrawingInputOpen(true);
                }
              }}
            />
            {/* Only show mask editing overlay if model requires mask */}
            {modelRequiresMask && (
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center pointer-events-none">
                <div className="text-white text-center">
                  <div className="text-2xl mb-2">✏️</div>
                  <div className="text-sm font-medium">
                    {isMaskOverlay ? 'Click to edit mask' : 'Click to add mask'}
                  </div>
                </div>
              </div>
            )}
            <div className="absolute top-2 right-2 bg-dark-blue/80 px-2 py-1 rounded text-xs text-yellow flex items-center gap-1">
              <span>🖼️</span>
              <span>{isImage ? imageFormat : 'Image'}</span>
              {isMaskOverlay && modelRequiresMask && (
                <span className="ml-1 bg-purple-600 text-white px-1 rounded flex items-center gap-1">
                  <span>🎭</span> MASKED
                </span>
              )}
            </div>
            {/* Visual indicator showing masked areas - only show if model requires mask */}
            {isMaskOverlay && modelRequiresMask && (
              <div className="absolute bottom-2 left-2 right-2 bg-purple-900/80 text-white text-xs px-2 py-1 rounded">
                Purple areas will be regenerated
              </div>
            )}
          </div>
          <p className="text-white/60 text-sm mt-2">
            {modelRequiresMask ? (
              isMaskOverlay
                ? "✅ Mask applied - Purple areas show where changes will occur"
                : "📸 Image uploaded - Click to add mask for inpainting"
            ) : (
              "📸 Image uploaded successfully"
            )}
          </p>
        </div>
      );
    }

    // Fallback audio preview (detected by signature)
    if (shouldShowAudio) {
      return (
        <div className="mt-3 text-center">
          <div className="inline-block relative group">
            <div className="bg-dark-purple/40 p-4 rounded-lg border border-yellow/30">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">🎵</span>
                <span className="text-yellow font-medium">Audio File</span>
              </div>
              <audio
                src={urlAllowed && isUrl(data) ? data : `data:audio/mpeg;base64,${data}`}
                controls
                className="max-w-full"
              />
            </div>
            <div className="absolute top-2 right-2 bg-dark-blue/80 px-2 py-1 rounded text-xs text-yellow flex items-center gap-1">
              <span>🎵</span>
              <span>{isAudio ? audioFormat : 'Audio'}</span>
            </div>
            <p className="text-white/60 text-sm mt-2">Audio uploaded successfully</p>
          </div>
        </div>
      );
    }

    // Fallback video preview (detected by signature)
    if (shouldShowVideo) {
      return (
        <div className="mt-3 text-center">
          <div className="inline-block relative group">
            <Video
              image={`${data}`}
              className="w-[300px] h-auto rounded-lg border-2 border-yellow/30 shadow-lg"
              controls
              hideToolbar={true}
            />
            <div className="absolute top-2 right-2 bg-dark-blue/80 px-2 py-1 rounded text-xs text-yellow flex items-center gap-1">
              <span>🎬</span>
              <span>{isVideo ? videoFormat : 'Video'}</span>
            </div>
          </div>
          <p className="text-white/60 text-sm mt-2">Video uploaded successfully</p>
        </div>
      );
    }

    // No preview available
    return null;
  };

  // Function to extract node name from title
  const extractNodeName = (title) => {
    if (!title) return 'Other';
    // Match patterns like "NodeName [index]: fieldname" or "NodeName: fieldname"
    const match = title.match(/^(.+?):\s*/);
    return match ? match[1].trim() : 'Other';
  };

  // Function to get icon for node type
  const getNodeIcon = (nodeName) => {
    const lowerName = nodeName.toLowerCase();
    if (lowerName.includes('sampler')) return '🎯';
    if (lowerName.includes('clip') || lowerName.includes('text')) return '✏️';
    if (lowerName.includes('vae')) return '🎨';
    if (lowerName.includes('image') || lowerName.includes('latent')) return '🖼️';
    if (lowerName.includes('checkpoint') || lowerName.includes('model')) return '🤖';
    if (lowerName.includes('conditioning')) return '🔗';
    if (lowerName.includes('upscale')) return '🔍';
    return '📦';
  };

  // Debug logging for schema analysis
  // console.log('[DynamicPromptForm] Schema analysis:', {
  //   isComfyUIModel,
  //   schemaKeys: Object.keys(schema),
  //   hasScheduler: 'scheduler' in schema,
  //   schedulerMapping: schema.scheduler
  // });

  // Separate inputs by importance and group by node
  const mainInputs = [];
  const advancedInputsByNode = {};
  
  Object.entries(schema).forEach(([key, mapping]) => {
    const importance = mapping.importance || 3; // Default to 3 if not specified
    const input = renderInput(key, mapping);
    
    // Debug logging for each field
    // if (key === 'scheduler') {
    //   console.log('[DynamicPromptForm] Processing scheduler field:', {
    //     key,
    //     importance,
    //     isComfyUIModel,
    //     willBeInMainInputs: !isComfyUIModel || importance > 3,
    //     input: input ? 'rendered' : 'null'
    //   });
    // }
    
    if (input) { // Only add if renderInput returns something (not null for cosyflow)
      // If not a ComfyUI model, treat all inputs as main inputs
      if (!isComfyUIModel || importance > 3) {
        // if (key === 'scheduler') {
        //   console.log('[DynamicPromptForm] Adding scheduler to mainInputs');
        // }
        mainInputs.push(<div key={key}>{input}</div>);
      } else {
        // Group advanced inputs by node name (only for ComfyUI models)
        const nodeName = extractNodeName(mapping.title);
        if (!advancedInputsByNode[nodeName]) {
          advancedInputsByNode[nodeName] = [];
        }
        advancedInputsByNode[nodeName].push({ key, input });
      }
    }
  });

  // Convert grouped inputs to flat array for counting
  const advancedInputsCount = Object.values(advancedInputsByNode).reduce((total, inputs) => total + inputs.length, 0);

  // console.log('[DynamicPromptForm] Final render state:', {
  //   mainInputsCount: mainInputs.length,
  //   advancedInputsCount,
  //   mainInputsKeys: mainInputs.map((input, idx) => input.key)
  // });

  return (
    <>
      <form className="flex flex-col gap-5">
        {/* Main inputs section */}
        <div className="flex flex-row flex-wrap gap-5">
          {mainInputs}
        </div>
        
        {/* Advanced settings button - only show if there are advanced inputs */}
        {advancedInputsCount > 0 && (
          <button
            type="button"
            onClick={() => setShowAdvancedModal(true)}
            className="flex items-center gap-3 px-6 py-3 bg-dark-blue hover:bg-dark-purple border border-yellow/30 hover:border-yellow text-white hover:text-yellow rounded-xl transition-all duration-200 w-fit font-medium shadow-lg"
          >
            <span>⚙️</span>
            <span className="text-base">More Settings</span>
            <span className="text-sm bg-yellow/20 px-2 py-1 rounded-full">
              {advancedInputsCount}
            </span>
          </button>
        )}
        
        {/* Advanced inputs - always hidden in main form, only shown in modal */}
        {/* No longer needed since inputs are only rendered in the modal */}
      </form>
      
      {/* DrawingInput Modal for masking */}
      {drawingInputOpen && drawingInputImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-dark-blue/40 backdrop-blur">
          <div className="relative">
            {/* Close button positioned absolutely */}
            <div className="absolute -top-12 right-0 z-10">
              <button 
                className="px-5 py-2 rounded bg-dark-blue text-white hover:text-yellow transition"
                onClick={() => {
                  setDrawingInputOpen(false);
                  setDrawingInputImage(null);
                  setDrawingInputFieldKey(null);
                }}
              >
                <svg className="h-7 w-7" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            
            {/* DrawingInput component */}
            <DrawingInput 
              image={drawingInputImage && drawingInputImage.includes(":\\") ? 
                toFileUrl(drawingInputImage) : 
                `data:image/png;base64,${drawingInputImage}`
              }
              originalImage={drawingInputImage}
              isMask={true}
              setImage={async (maskedData) => {
                // console.log('[DynamicPromptForm] Received mask from DrawingInput');
                
                if (drawingInputFieldKey) {
                  // Look for mask fields in the schema using their properties (not names)
                  const currentSchema = schema || {};
                  const maskFields = findMaskFields(currentSchema);
                  let maskFieldKey = maskFields.length > 0 ? maskFields[0] : null;
                  
                  if (maskFieldKey) {
                    // --- EXISTING LOGIC for separate mask field ---
                    // console.log(`[DynamicPromptForm] Found separate mask field: ${maskFieldKey}. Storing B&W mask.`);
                    setValue(maskFieldKey, maskedData);
                    
                    // Mark that we have a mask for this image
                    setMaskedFields(prev => ({
                      ...prev,
                      [drawingInputFieldKey]: true
                    }));
                    
                    // Create a visual overlay preview for the UI
                    const createMaskOverlay = async () => {
                      try {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        
                        const img = new Image();
                        img.src = `data:image/png;base64,${drawingInputImage}`;
                        await new Promise((resolve) => { img.onload = resolve; });
                        
                        canvas.width = img.width;
                        canvas.height = img.height;
                        ctx.drawImage(img, 0, 0);
                        
                        const maskImg = new Image();
                        maskImg.src = `data:image/png;base64,${maskedData}`;
                        await new Promise((resolve) => { maskImg.onload = resolve; });
                        
                        const maskCanvas = document.createElement('canvas');
                        const maskCtx = maskCanvas.getContext('2d');
                        maskCanvas.width = img.width;
                        maskCanvas.height = img.height;
                        maskCtx.drawImage(maskImg, 0, 0, img.width, img.height);
                        
                        const maskPixelData = maskCtx.getImageData(0, 0, img.width, img.height).data;
                        
                        ctx.fillStyle = 'rgba(147, 51, 234, 0.5)'; // Purple overlay
                        for (let i = 0; i < maskPixelData.length; i += 4) {
                          if (maskPixelData[i] > 128) {
                            const x = (i / 4) % img.width;
                            const y = Math.floor((i / 4) / img.width);
                            ctx.fillRect(x, y, 1, 1);
                          }
                        }
                        
                        const compositeBase64 = canvas.toDataURL('image/png').split(',')[1];
                        setMaskOverlayPreviews(prev => ({
                          ...prev,
                          [drawingInputFieldKey]: compositeBase64
                        }));
                      } catch (error) {
                        console.error('[DynamicPromptForm] Error creating mask overlay:', error);
                      }
                    };
                    
                    createMaskOverlay();
                    
                    if (onFieldChange) onFieldChange(maskFieldKey, maskedData);
                    if (onB64Upload) onB64Upload(maskFieldKey, maskedData);

                  } else {
                    // --- NEW LOGIC for merging into alpha channel ---
                    // console.log('[DynamicPromptForm] No separate mask field found. Merging mask into alpha channel of the main image.');
        
                    try {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
        
                        const originalImg = new Image();
                        originalImg.src = `data:image/png;base64,${drawingInputImage}`;
                        await new Promise((resolve, reject) => {
                            originalImg.onload = resolve;
                            originalImg.onerror = reject;
                        });
        
                        canvas.width = originalImg.width;
                        canvas.height = originalImg.height;
        
                        const maskImg = new Image();
                        maskImg.src = `data:image/png;base64,${maskedData}`;
                        await new Promise((resolve, reject) => {
                            maskImg.onload = resolve;
                            maskImg.onerror = reject;
                        });
        
                        ctx.drawImage(originalImg, 0, 0);
        
                        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                        const pixels = imageData.data;
        
                        const maskCanvas = document.createElement('canvas');
                        const maskCtx = maskCanvas.getContext('2d');
                        maskCanvas.width = canvas.width;
                        maskCanvas.height = canvas.height;
                        maskCtx.drawImage(maskImg, 0, 0, canvas.width, canvas.height);
                        const maskPixels = maskCtx.getImageData(0, 0, canvas.width, canvas.height).data;
        
                        for (let i = 0; i < pixels.length; i += 4) {
                            if (maskPixels[i] > 200) { // Check for white in mask
                                pixels[i + 3] = 0; // Set alpha to 0 (transparent)
                            }
                        }
        
                        ctx.putImageData(imageData, 0, 0);
        
                        const newBase64 = canvas.toDataURL('image/png').split(',')[1];
        
                        // console.log(`[DynamicPromptForm] Updating main image field '${drawingInputFieldKey}' with RGBA image.`);
                        setValue(drawingInputFieldKey, newBase64);
                        if (onB64Upload) {
                            onB64Upload(drawingInputFieldKey, newBase64);
                        }
        
                        setMaskedFields(prev => ({ ...prev, [drawingInputFieldKey]: true }));
                        setMaskOverlayPreviews(prev => ({ ...prev, [drawingInputFieldKey]: newBase64 }));
        
                    } catch (error) {
                        console.error('[DynamicPromptForm] Error merging mask into alpha channel:', error);
                    }
                  }
                }
                
                // Close the modal
                setDrawingInputOpen(false);
                setDrawingInputImage(null);
                setDrawingInputFieldKey(null);
              }}
              closeEvent={() => {
                setDrawingInputOpen(false);
                setDrawingInputImage(null);
                setDrawingInputFieldKey(null);
              }}
            />
          </div>
        </div>
      )}
      
      {/* More Settings Modal */}
      {showAdvancedModal && (
        <Modal 
          heading={
            <div className="flex items-center gap-3 text-yellow">
              <span>⚙️</span>
              <span className="font-bold">More Settings</span>
              <span className="text-sm bg-yellow/20 text-yellow px-2 py-1 rounded-full font-medium">
                {advancedInputsCount} parameters
              </span>
            </div>
          }
          closeEvent={() => setShowAdvancedModal(false)}
        >
          <div className="bg-dark-blue border-x border-b border-dark-purple rounded-b-xl overflow-hidden shadow-2xl">
            {/* Subheader with description */}
            <div className="bg-dark-purple/30 px-6 py-3 border-b border-yellow/10">
              <div className="text-white/70 text-base text-center">
                💡 Fine-tune your generation parameters for optimal results
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              <div className="space-y-8">
                {/* Render grouped inputs by node */}
                {Object.entries(advancedInputsByNode).map(([nodeName, inputs], nodeIndex) => (
                  <div key={`node-${nodeIndex}`} className="bg-dark-purple/20 border border-yellow/20 rounded-xl overflow-hidden">
                    {/* Node Header */}
                    <div className="bg-gradient-to-r from-yellow/20 to-yellow/10 px-4 py-3 border-b border-yellow/20">
                      <h3 className="text-yellow font-semibold text-lg flex items-center gap-2">
                        <span className="text-xl">{getNodeIcon(nodeName)}</span>
                        {nodeName}
                        <span className="text-sm bg-yellow/20 px-2 py-1 rounded-full ml-auto">
                          {inputs.length} {inputs.length === 1 ? 'parameter' : 'parameters'}
                        </span>
                      </h3>
                    </div>
                    
                    {/* Node Fields */}
                    <div className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {inputs.map(({ key, input }) => (
                          <div key={key} className="bg-dark-purple/30 border border-white/10 rounded-lg p-4 hover:bg-dark-purple/40 transition-all duration-200">
                            {input}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="bg-dark-purple/30 px-6 py-4 border-t border-yellow/20 flex justify-between items-center">
              <div className="text-white/60 text-base">
                All changes are applied automatically
              </div>
              <button 
                type="button"
                onClick={() => setShowAdvancedModal(false)}
                className="px-6 py-3 bg-yellow hover:bg-yellow/90 text-dark-blue font-medium rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Done
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
})

export default DynamicPromptForm